import { useState, useMemo, useEffect } from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { doc, getDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";

// Firebase
import { db, auth, GoogleAuthProvider, signInWithPopup, signOut } from './firebaseUtils.js';

// Components
import Layout from './Layout.js';
import Dashboard from './Dashboard.js';
import CreateQuote from './CreateQuote.js';
import CreateInvoice from './CreateInvoice.js';
import Quotes from './Quotes.js';
import Invoices from './Invoices.js';
import QuoteDetail from './QuoteDetail.js';
import InvoiceDetail from './InvoiceDetail.js';
import ProformaInvoice from './ProformaInvoice.js'; 
import Login from './Login.js';
import CreateOrder from './CreateOrder.js';
import Orders from './Orders.js';
import OrderDetail from './OrderDetail.js';
import Spinner from './Spinner.js';
import AccountsDashboard from './AccountsDashboard.js';
import TransactionLedger from './TransactionLedger.js';
import AccountsInvoices from './AccountsInvoices.js';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Persistence and Authorization Check
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        // Double check authorization against Firestore on refresh
        const userData = await checkAuthorization(currentUser.email);
        if (userData) {
          setUser({ ...currentUser, role: userData.role || 'sales' });
        } else {
          await signOut(auth);
          setUser(null);
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const checkAuthorization = async (email) => {
    try {
      const userRef = doc(db, "authorized_users", email);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        return userSnap.data(); // Return the whole data object containing the role
      }
      return null;
    } catch (error) {
      console.error("Auth check failed:", error);
      return null;
    }
  };

  const handleLogin = async () => {
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      const loggedInUser = result.user;

      const userData = await checkAuthorization(loggedInUser.email);

      if (userData) {
        setUser({ ...loggedInUser, role: userData.role || 'sales' });
      } else {
        await signOut(auth);
        alert("Access Denied: Your email is not whitelisted in the system.");
      }
    } catch (error) {
      console.error("Login failed:", error);
      alert("Login failed. Please try again.");
    }
  };

  const handleLogout = () => {
    signOut(auth).then(() => setUser(null));
  };

  const router = useMemo(() => createBrowserRouter([
    {
      path: "/",
      element: <Layout user={user} handleLogout={handleLogout} db={db} />,
      children: [
        { path: "/", element: user?.role === 'accounts' ? <AccountsDashboard db={db} user={user} /> : <Dashboard db={db} user={user} /> },
        // Sales-only routes
        { path: "/quotes/new", element: user?.role === 'sales' ? <CreateQuote db={db} /> : <Navigate to="/" /> },
        { path: "/quotes", element: user?.role === 'sales' ? <Quotes db={db} /> : <Navigate to="/" /> },
        { path: "/quotes/:id", element: user?.role === 'sales' ? <QuoteDetail db={db} /> : <Navigate to="/" /> },
        { path: "/quotes/:id/pi", element: user?.role === 'sales' ? <ProformaInvoice db={db} /> : <Navigate to="/" /> },
        { path: "/invoices/new", element: user?.role === 'sales' ? <CreateInvoice db={db} /> : <Navigate to="/" /> },
        { path: "/invoices", element: user?.role === 'sales' ? <Invoices db={db} /> : <Navigate to="/" /> },
        { path: "/invoices/:id", element: user?.role === 'sales' ? <InvoiceDetail db={db} /> : <Navigate to="/" /> },
        { path: "/orders/new", element: user?.role === 'sales' ? <CreateOrder db={db} /> : <Navigate to="/" /> },
        // Accounts-only specific module routes (dashboard handled by root path)
        { path: "/accounts/transactions", element: user?.role === 'accounts' ? <TransactionLedger db={db} /> : <Navigate to="/" /> },
        { path: "/accounts/invoices", element: user?.role === 'accounts' ? <AccountsInvoices db={db} /> : <Navigate to="/" /> },
        // Common routes (Order Module has its own internal role filtering)
        { path: "/orders", element: user?.role === 'accounts' ? <Navigate to="/" /> : <Orders db={db} user={user} /> },
        { path: "/orders/:id", element: user?.role === 'accounts' ? <Navigate to="/" /> : <OrderDetail db={db} user={user} /> },
        { path: "*", element: <Navigate to="/" /> },
      ],
    },
  ]), [user, handleLogout]);

  if (loading) {
    return <div style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}><Spinner /></div>;
  }

  // 1. LOGIN SCREEN (If not logged in)
  if (!user) {
    return <Login handleLogin={handleLogin} />;
  }

  // 2. APP SCREEN (If logged in)
  return <RouterProvider router={router} />;
}

export default App;